import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../config/api';
import useAuthStore from '../../store/authStore';
import { useFeatures } from '../../hooks/useFeatures';

const DOC_TYPES = {
  100: 'Purchase In', 120: 'Purchase Return',
  200: 'Sales Out',   220: 'Sales Return',
  230: 'Proforma',    300: 'Inventory Count', 400: 'Loss/Damage'
};

const STATUS_COLORS = {
  draft:     'bg-yellow-100 text-yellow-700',
  confirmed: 'bg-green-100 text-green-700',
  cancelled: 'bg-red-100 text-red-700',
};

function PayBadge({ total, paid }) {
  if (!total) return null;
  if (paid >= total) return <span className="px-1.5 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-700">Paid</span>;
  if (paid > 0)      return <span className="px-1.5 py-0.5 rounded text-xs font-semibold bg-yellow-100 text-yellow-700">Partial</span>;
  return               <span className="px-1.5 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-700">Unpaid</span>;
}

export default function AccountLedger() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  const features = useFeatures();
  const dual = !!features.second_currency;
  const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: () => api.get('/api/settings').then(r => r.data) });
  const RATE_FALLBACK = 89500;
  const rate = Number(settings?.exchange_rate) || RATE_FALLBACK;
  const showLBP = settings?.currency?.show_lbp ?? true;
  const defaultPayCur = (showLBP && !dual) ? 'LBP' : 'USD';
  const toUSD = (amt, cur, r) => cur === 'LBP' ? (Number(amt) || 0) / (Number(r) || rate) : (Number(amt) || 0);
  const fmtMoney = (amt, cur) => cur === 'LBP'
    ? `${Math.round(Number(amt) || 0).toLocaleString('en-US')} LBP`
    : `$${(Number(amt) || 0).toFixed(2)}`;

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');
  const [activeTab, setActiveTab] = useState('documents'); // documents | payments

  // Payment form state
  const [payDocId,    setPayDocId]    = useState('');
  const [payAmount,   setPayAmount]   = useState('');
  const [payMethodId, setPayMethodId] = useState('');
  const [payCurrency, setPayCurrency] = useState(defaultPayCur);
  const [payNotes,    setPayNotes]    = useState('');
  const [showPayForm, setShowPayForm] = useState(false);

  const { data: account } = useQuery({
    queryKey: ['account', id],
    queryFn: () => api.get(`/api/accounts/${id}`).then(r => r.data),
  });

  const { data: allDocs = [], refetch: refetchDocs } = useQuery({
    queryKey: ['account-docs', id, dateFrom, dateTo],
    queryFn: () => api.get('/api/documents', {
      params: {
        account_id: id,
        all: true,
        ...(dateFrom && { date_from: dateFrom }),
        ...(dateTo   && { date_to:   dateTo   }),
      }
    }).then(r => Array.isArray(r.data) ? r.data : (r.data?.data || [])),
    enabled: !!id,
  });

  const { data: allPayments = [], refetch: refetchPayments } = useQuery({
    queryKey: ['account-payments', id],
    queryFn: async () => {
      // fetch payments for each confirmed document
      const confirmed = allDocs.filter(d => d.status === 'confirmed');
      if (confirmed.length === 0) return [];
      const results = await Promise.all(
        confirmed.map(d => api.get(`/api/documents/${d._id}/payments`).then(r => r.data.map(p => ({ ...p, document_number: d.number, doc_type: d.type }))))
      );
      return results.flat();
    },
    enabled: allDocs.length > 0,
  });

  const { data: paymentMethods = [] } = useQuery({
    queryKey: ['payment-methods'],
    queryFn: () => api.get('/api/payment-methods').then(r => r.data),
  });

  const addPaymentMutation = useMutation({
    mutationFn: ({ _docId, ...data }) => api.post(`/api/documents/${_docId}/payments`, data),
    onSuccess: () => {
      refetchDocs();
      refetchPayments();
      queryClient.invalidateQueries(['account-payments', id]);
      setPayAmount('');
      setPayNotes('');
      setPayDocId('');
      setShowPayForm(false);
    },
    onError: (err) => {
      const data = err.response?.data;
      if (data?.reason === 'overpayment') {
        alert(`Payment exceeds the remaining balance of ${fmtMoney(data.remaining_balance, data.currency)}.`);
      } else {
        alert(data?.error || 'Error adding payment');
      }
    },
  });

  const voidPaymentMutation = useMutation({
    mutationFn: ({ docId, paymentId, reason }) => api.delete(`/api/documents/${docId}/payments/${paymentId}`, { data: { reason } }),
    onSuccess: () => { refetchDocs(); refetchPayments(); },
    onError: (err) => alert(err.response?.data?.error || 'Error voiding payment'),
  });

  const handleVoidPayment = (p) => {
    const reason = window.prompt('Reason for voiding this payment (optional):', '');
    if (reason === null) return; // cancelled
    voidPaymentMutation.mutate({ docId: p.document_id, paymentId: p._id, reason });
  };

  // Direction: money coming IN to us (customer pays us / supplier refunds us)
  // vs money going OUT from us (we pay supplier / we refund customer).
  // Sales (200) and Purchase Returns (120) are receivables — the balance owed is owed TO us.
  // Purchases (100) and Sales Returns (220) are payables — the balance owed is owed BY us.
  const isInboundDoc = (type) => type === 200 || type === 120;
  const DirectionTag = ({ type, className = '' }) => isInboundDoc(type)
    ? <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-semibold bg-green-100 text-green-700 whitespace-nowrap ${className}`}>↓ They pay you</span>
    : <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-semibold bg-orange-100 text-orange-700 whitespace-nowrap ${className}`}>↑ You pay them</span>;

  const confirmedDocs = allDocs.filter(d => d.status === 'confirmed');
  // summary totals in USD-equivalent (each doc converted at its own snapshot rate)
  const totalBilled   = confirmedDocs.reduce((s, d) => s + toUSD(d.totals?.total   || 0, d.currency, d.exchange_rate_snapshot), 0);
  const totalPaid     = confirmedDocs.reduce((s, d) => s + toUSD(d.totals?.paid    || 0, d.currency, d.exchange_rate_snapshot), 0);
  // net balance, signed: positive = they owe you (receivable), negative = you owe them (payable)
  const netBalance = confirmedDocs.reduce((s, d) => {
    const bal = toUSD(d.totals?.balance || 0, d.currency, d.exchange_rate_snapshot);
    return s + (isInboundDoc(d.type) ? bal : -bal);
  }, 0);
  const paymentsTotalUSD = (arr) => arr.filter(p => p.status === 'completed').reduce((s, p) => s + toUSD(p.amount, p.currency, p.exchange_rate_snapshot), 0);

  const unpaidDocs = confirmedDocs.filter(d => (d.totals?.balance || 0) > 0);

  // Auto-set currency to match the target doc whenever the form opens or unpaid docs load
  const autoTargetDoc = payDocId ? unpaidDocs.find(d => d._id === payDocId) : unpaidDocs[unpaidDocs.length - 1];
  useEffect(() => {
    if (showPayForm && autoTargetDoc) setPayCurrency(autoTargetDoc.currency || defaultPayCur);
  }, [showPayForm, autoTargetDoc?._id]); // eslint-disable-line

  // Remaining balance (in the payment's own currency) for the currently targeted document —
  // used to warn before submitting an overpayment.
  const remainingForTarget = autoTargetDoc
    ? toUSD(autoTargetDoc.totals?.balance || 0, autoTargetDoc.currency, autoTargetDoc.exchange_rate_snapshot)
    : null;
  const payAmountUSD = payAmount ? toUSD(Number(payAmount), payCurrency, rate) : 0;
  const isOverpaying = remainingForTarget != null && payAmountUSD > remainingForTarget + 0.01;

  const handleAddPayment = () => {
    if (!payAmount || !payMethodId) return;
    const targetDocId = payDocId || (unpaidDocs.length > 0 ? unpaidDocs[unpaidDocs.length - 1]._id : null);
    if (!targetDocId) return alert('No unpaid documents to apply this payment to.');
    if (isOverpaying) {
      const remainingDisplay = autoTargetDoc ? fmtMoney(autoTargetDoc.totals?.balance || 0, autoTargetDoc.currency) : '';
      if (!window.confirm(`This payment is more than the remaining balance (${remainingDisplay}). Record it anyway as an overpayment?`)) {
        return;
      }
    }
    addPaymentMutation.mutate({
      _docId: targetDocId,
      payment_method_id: payMethodId,
      amount: Number(payAmount),
      currency: payCurrency,
      notes: payNotes,
      allow_overpayment: isOverpaying || undefined,
    });
  };

  const inputCls = "w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white";

  const fmtDocDate = (d) => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

  return (
    <div className="p-3 md:p-6 text-gray-800">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/accounts')} className="text-gray-500 hover:text-gray-700 text-sm font-medium">← Back</button>
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">{account?.name || 'Account Ledger'}</h1>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-gray-400 text-sm capitalize">{account?.type}</span>
            {account?.roles?.map(r => (
              <span key={r} className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium capitalize">{r}</span>
            ))}
            {account?.phone && <span className="text-gray-400 text-sm">· {account.phone}</span>}
          </div>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-2 md:gap-4 mb-5">
        <div className="bg-white rounded-xl border border-gray-200 p-2.5 md:p-4 shadow-sm">
          <p className="text-xs text-gray-500 mb-1">Total Billed</p>
          <p className="text-base md:text-2xl font-bold text-gray-800">${totalBilled.toFixed(2)}</p>
        </div>
        <div className="bg-white rounded-xl border border-green-200 p-2.5 md:p-4 shadow-sm">
          <p className="text-xs text-green-600 mb-1">Total Paid</p>
          <p className="text-base md:text-2xl font-bold text-green-600">${totalPaid.toFixed(2)}</p>
        </div>
        <div className={`bg-white rounded-xl border p-2.5 md:p-4 shadow-sm ${netBalance === 0 ? 'border-green-200' : netBalance > 0 ? 'border-green-200' : 'border-red-200'}`}>
          <p className={`text-xs mb-1 ${netBalance === 0 ? 'text-green-600' : netBalance > 0 ? 'text-green-600' : 'text-red-500'}`}>
            {netBalance === 0 ? 'Balance' : netBalance > 0 ? 'They owe you' : 'You owe them'}
          </p>
          <p className={`text-base md:text-2xl font-bold ${netBalance === 0 ? 'text-green-600' : netBalance > 0 ? 'text-green-600' : 'text-red-600'}`}>
            ${Math.abs(netBalance).toFixed(2)}
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4 flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span>From</span>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none" />
          <span>To</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none" />
        </div>
        {(dateFrom || dateTo) && (
          <button onClick={() => { setDateFrom(''); setDateTo(''); }}
            className="text-sm text-blue-500 hover:text-blue-700">Clear</button>
        )}
        <div className="ml-auto">
          <button onClick={() => setShowPayForm(!showPayForm)}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg">
            + Add Payment
          </button>
        </div>
      </div>

      {/* Add Payment Form */}
      {showPayForm && (
        <div className="bg-white border border-blue-200 rounded-xl p-4 mb-4 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-bold text-gray-700">Record Payment</p>
            {autoTargetDoc && <DirectionTag type={autoTargetDoc.type} />}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Document *</label>
              <select value={payDocId} onChange={e => {
                setPayDocId(e.target.value);
                const doc = unpaidDocs.find(d => d._id === e.target.value);
                if (doc) setPayCurrency(doc.currency || defaultPayCur);
              }} className={inputCls}>
                <option value="">(auto — oldest unpaid)</option>
                {unpaidDocs.map(d => (
                  <option key={d._id} value={d._id}>
                    {d.number} — Balance: {fmtMoney(d.totals?.balance || 0, d.currency)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Method *</label>
              <select value={payMethodId} onChange={e => setPayMethodId(e.target.value)} className={inputCls}>
                <option value="">Select method</option>
                {paymentMethods.map(m => <option key={m._id} value={m._id}>{m.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Amount *</label>
              <input type="number" value={payAmount} min="0" step="0.01"
                onChange={e => setPayAmount(e.target.value)} className={inputCls} placeholder="0.00" />
              {remainingForTarget != null && (
                <p className={`text-[11px] mt-1 ${isOverpaying ? 'text-red-500 font-semibold' : 'text-gray-400'}`}>
                  Remaining: {autoTargetDoc ? fmtMoney(autoTargetDoc.totals?.balance || 0, autoTargetDoc.currency) : '—'}
                  {isOverpaying ? ' — exceeds balance' : ''}
                </p>
              )}
            </div>
            {showLBP && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Currency {autoTargetDoc ? <span className="text-gray-400 font-normal">(locked to document)</span> : null}
                </label>
                <select
                  value={payCurrency}
                  onChange={e => setPayCurrency(e.target.value)}
                  disabled={!!autoTargetDoc}
                  className={`${inputCls} ${autoTargetDoc ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                >
                  <option value="USD">USD</option>
                  <option value="LBP">LBP</option>
                </select>
              </div>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
              <input type="text" value={payNotes} onChange={e => setPayNotes(e.target.value)}
                className={inputCls} placeholder="Optional" />
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={handleAddPayment}
              disabled={!payAmount || !payMethodId || addPaymentMutation.isPending}
              className={`px-4 py-2 text-white text-sm font-medium rounded-lg disabled:opacity-50 ${isOverpaying ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
              {addPaymentMutation.isPending ? 'Saving...' : isOverpaying ? 'Record Overpayment' : 'Record Payment'}
            </button>
            <button onClick={() => setShowPayForm(false)}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-gray-200">
        {['documents', 'payments'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 text-sm font-medium capitalize transition-colors ${activeTab === tab ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}>
            {tab} {tab === 'documents' ? `(${allDocs.length})` : `(${allPayments.length})`}
          </button>
        ))}
      </div>

      {/* Documents Tab — mobile cards */}
      {activeTab === 'documents' && (
        <div className="md:hidden space-y-2">
          {allDocs.length === 0 && <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-400 text-sm">No documents found.</div>}
          {allDocs.map(doc => (
            <div key={doc._id} onClick={() => navigate(`/documents/${doc._id}`)}
                 className="bg-white rounded-xl border border-gray-200 shadow-sm p-3 active:bg-gray-50">
              <div className="flex items-center justify-between mb-1">
                <span className="font-mono text-blue-600 font-semibold text-sm">{doc.number || <span className="text-gray-400 italic">Draft</span>}</span>
                <div className="flex items-center gap-1.5">
                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_COLORS[doc.status]}`}>{doc.status}</span>
                  <PayBadge total={doc.totals?.total} paid={doc.totals?.paid || 0} />
                </div>
              </div>
              <div className="text-xs text-gray-500 mb-2">{DOC_TYPES[doc.type]} · {fmtDocDate(doc.date)}</div>
              <div className="flex justify-between text-xs">
                <span>Total <b className="text-gray-800">{fmtMoney(doc.totals?.total || 0, doc.currency)}</b></span>
                <span className="text-green-600">Paid <b>{fmtMoney(doc.totals?.paid || 0, doc.currency)}</b></span>
                <span className="text-red-500">Bal <b>{fmtMoney(doc.totals?.balance || 0, doc.currency)}</b></span>
              </div>
            </div>
          ))}
          {allDocs.length > 0 && (
            <div className="bg-gray-50 rounded-xl border border-gray-200 p-3 flex justify-between text-xs font-bold">
              <span className="text-gray-600">Total ({confirmedDocs.length})</span>
              <span>${totalBilled.toFixed(2)}</span>
              <span className="text-green-600">${totalPaid.toFixed(2)}</span>
              <span className={netBalance >= 0 ? 'text-green-600' : 'text-red-500'}>
                {netBalance >= 0 ? 'They owe ' : 'You owe '}${Math.abs(netBalance).toFixed(2)}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Documents Tab — desktop table */}
      {activeTab === 'documents' && (
        <div className="hidden md:block bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Number</th>
                <th className="text-left px-4 py-3 font-semibold">Type</th>
                <th className="text-left px-4 py-3 font-semibold">Date</th>
                <th className="text-right px-4 py-3 font-semibold">Total</th>
                <th className="text-right px-4 py-3 font-semibold">Paid</th>
                <th className="text-right px-4 py-3 font-semibold">Balance</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold">Payment</th>
                <th className="w-16"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {allDocs.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-gray-400">No documents found.</td></tr>
              ) : allDocs.map(doc => (
                <tr key={doc._id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-4 py-2.5 font-mono text-blue-600 font-semibold">
                    {doc.number || <span className="text-gray-400 italic font-normal">Draft</span>}
                  </td>
                  <td className="px-4 py-2.5 text-gray-600">{DOC_TYPES[doc.type]}</td>
                  <td className="px-4 py-2.5 text-gray-500 text-xs">
                    {new Date(doc.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </td>
                  <td className="px-4 py-2.5 text-right font-medium text-gray-800">{fmtMoney(doc.totals?.total || 0, doc.currency)}</td>
                  <td className="px-4 py-2.5 text-right text-green-600">{fmtMoney(doc.totals?.paid || 0, doc.currency)}</td>
                  <td className="px-4 py-2.5 text-right font-bold text-red-500">{fmtMoney(doc.totals?.balance || 0, doc.currency)}</td>
                  <td className="px-4 py-2.5">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[doc.status]}`}>
                      {doc.status}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    <PayBadge total={doc.totals?.total} paid={doc.totals?.paid || 0} />
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <button onClick={() => navigate(`/documents/${doc._id}`)}
                      className="text-blue-500 hover:text-blue-700 text-xs font-medium">
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            {allDocs.length > 0 && (
              <tfoot className="bg-gray-50 border-t border-gray-200 text-sm font-semibold">
                <tr>
                  <td colSpan={3} className="px-4 py-3 text-gray-600">Total ({confirmedDocs.length} confirmed)</td>
                  <td className="px-4 py-3 text-right text-gray-800">${totalBilled.toFixed(2)}</td>
                  <td className="px-4 py-3 text-right text-green-600">${totalPaid.toFixed(2)}</td>
                  <td className={`px-4 py-3 text-right ${netBalance >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                    {netBalance >= 0 ? 'They owe ' : 'You owe '}${Math.abs(netBalance).toFixed(2)}
                  </td>
                  <td colSpan={3}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {/* Payments Tab — mobile cards */}
      {activeTab === 'payments' && (
        <div className="md:hidden space-y-2">
          {allPayments.length === 0 && <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-400 text-sm">No payments recorded.</div>}
          {allPayments.map(p => (
            <div key={p._id} className={`bg-white rounded-xl border shadow-sm p-3 ${p.status === 'voided' ? 'border-gray-200 opacity-60' : p.is_refund ? 'border-orange-200' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="font-mono text-blue-600 font-semibold text-sm">{p.document_number}</span>
                <span className={`font-bold ${p.amount < 0 ? 'text-orange-600' : isInboundDoc(p.doc_type) ? 'text-green-600' : 'text-orange-600'}`}>
                  {p.amount < 0 ? '−' : isInboundDoc(p.doc_type) ? '+' : '−'}{fmtMoney(Math.abs(p.amount), p.currency)}
                </span>
              </div>
              <div className="mb-1.5 flex items-center gap-1.5 flex-wrap">
                <DirectionTag type={p.doc_type} />
                {p.is_refund && <span className="px-1.5 py-0.5 rounded text-[11px] font-semibold bg-orange-100 text-orange-700">Refund</span>}
                {p.status === 'voided' && <span className="px-1.5 py-0.5 rounded text-[11px] font-semibold bg-gray-200 text-gray-600">Voided</span>}
                {(p.status === 'refunded' || p.refunded) && <span className="px-1.5 py-0.5 rounded text-[11px] font-semibold bg-orange-100 text-orange-700">Refunded</span>}
                {p.edit_history?.length > 0 && <span className="px-1.5 py-0.5 rounded text-[11px] font-semibold bg-blue-100 text-blue-700">Edited ×{p.edit_history.length}</span>}
              </div>
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>{new Date(p.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })} · {p.payment_method_name}{p.notes ? ` · ${p.notes}` : ''}</span>
                {user?.role === 'admin' && p.status === 'completed' && !p.refunded && (
                  <button onClick={() => handleVoidPayment(p)}
                    className="text-red-400 font-bold text-base px-1" title="Void payment">×</button>
                )}
              </div>
            </div>
          ))}
          {allPayments.length > 0 && (
            <div className="bg-gray-50 rounded-xl border border-gray-200 p-3 flex justify-between text-xs font-bold">
              <span className="text-gray-600">Total Payments</span>
              <span>{fmtMoney(paymentsTotalUSD(allPayments), 'USD')}</span>
            </div>
          )}
        </div>
      )}

      {/* Payments Tab — desktop table */}
      {activeTab === 'payments' && (
        <div className="hidden md:block bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Date</th>
                <th className="text-left px-4 py-3 font-semibold">Document</th>
                <th className="text-left px-4 py-3 font-semibold">Type</th>
                <th className="text-left px-4 py-3 font-semibold">Direction</th>
                <th className="text-left px-4 py-3 font-semibold">Method</th>
                <th className="text-left px-4 py-3 font-semibold">Currency</th>
                <th className="text-left px-4 py-3 font-semibold">Notes</th>
                <th className="text-right px-4 py-3 font-semibold">Amount</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {allPayments.length === 0 ? (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-gray-400">No payments recorded.</td></tr>
              ) : allPayments.map(p => (
                <tr key={p._id} className={`hover:bg-gray-50/50 ${p.status === 'voided' ? 'opacity-60' : ''}`}>
                  <td className="px-4 py-2.5 text-gray-500 text-xs">
                    {new Date(p.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-blue-600 font-semibold">{p.document_number}</td>
                  <td className="px-4 py-2.5 text-gray-500 text-xs">{p.is_refund ? 'Refund' : DOC_TYPES[p.doc_type]}</td>
                  <td className="px-4 py-2.5"><DirectionTag type={p.doc_type} /></td>
                  <td className="px-4 py-2.5 text-gray-700 font-medium">{p.payment_method_name}</td>
                  <td className="px-4 py-2.5 text-gray-500">{p.currency}</td>
                  <td className="px-4 py-2.5 text-gray-400 text-xs">{p.notes || '—'}</td>
                  <td className={`px-4 py-2.5 text-right font-bold ${p.amount < 0 ? 'text-orange-600' : isInboundDoc(p.doc_type) ? 'text-green-600' : 'text-orange-600'}`}>
                    {p.amount < 0 ? '−' : isInboundDoc(p.doc_type) ? '+' : '−'}{fmtMoney(Math.abs(p.amount), p.currency)}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${p.status === 'completed' ? 'bg-green-100 text-green-700' : p.status === 'voided' ? 'bg-gray-200 text-gray-600' : 'bg-orange-100 text-orange-700'}`}>
                        {p.refunded ? 'refunded' : p.status}
                      </span>
                      {p.edit_history?.length > 0 && (
                        <span className="px-1.5 py-0.5 rounded text-[11px] font-semibold bg-blue-100 text-blue-700" title={p.edit_history.map(h => `${h.edited_by_name || 'user'} @ ${new Date(h.edited_at).toLocaleString()}`).join('\n')}>
                          Edited ×{p.edit_history.length}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {user?.role === 'admin' && p.status === 'completed' && !p.refunded && (
                      <button
                        onClick={() => handleVoidPayment(p)}
                        className="text-red-400 hover:text-red-600 font-bold text-base" title="Void payment">×</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            {allPayments.length > 0 && (
              <tfoot className="bg-gray-50 border-t border-gray-200 text-sm font-semibold">
                <tr>
                  <td colSpan={7} className="px-4 py-3 text-gray-600">Total Payments</td>
                  <td className="px-4 py-3 text-right text-gray-800">
                    {fmtMoney(paymentsTotalUSD(allPayments), 'USD')}
                  </td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}
